import { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import Close from "@mui/icons-material/Close";
import EventBusy from "@mui/icons-material/EventBusy";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import dayjs from "dayjs";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";
import layout from "../../actprep-ui/constants/layout";

// Role choices matching the Django model
const COACH_ROLES = [
  { value: "Assistant Coach", label: "Assistant Coach" },
  { value: "Academic Coach", label: "Academic Coach" },
  { value: "Lead Coach", label: "Lead Coach" },
  { value: "One on One Coach", label: "One on One Coach" },
];

export default function RoleDrawer({
  open,
  onClose,
  role,
  coach,
  asOfDate,
  onSave,
}) {
  const [formData, setFormData] = useState({
    role: "",
    start_date: dayjs(),
    end_date: null,
    is_active: true,
  });
  const [dateRange, setDateRange] = useState([dayjs(), null]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditing = !!role?.uuid;

  useEffect(() => {
    if (open) {
      if (role) {
        // Editing existing role or duplicating
        setFormData({
          role: role.role || "",
          start_date: role.start_date ? dayjs(role.start_date) : dayjs(),
          end_date: role.end_date ? dayjs(role.end_date) : null,
          is_active: role.is_active !== undefined ? role.is_active : true,
        });
        setDateRange([
          role.start_date ? dayjs(role.start_date) : dayjs(),
          role.end_date ? dayjs(role.end_date) : null,
        ]);
      } else {
        // Adding new role
        setFormData({
          role: "",
          start_date: dayjs(),
          end_date: null,
          is_active: true,
        });
        setDateRange([dayjs(), null]);
      }
      setErrors({});
    }
  }, [open, role]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setFormData((prev) => ({
      ...prev,
      start_date: newRange[0],
      end_date: newRange[1],
    }));
  };

  const handleEndToday = () => {
    const today = asOfDate || dayjs();
    const newRange = [formData.start_date, today];
    setDateRange(newRange);
    setFormData((prev) => ({
      ...prev,
      end_date: today,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (
      formData.end_date &&
      formData.start_date &&
      formData.end_date.isBefore(formData.start_date, "day")
    ) {
      newErrors.end_date = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        coach_id: coach.uuid,
        role: formData.role,
        start_date: formData.start_date.format("YYYY-MM-DD"),
        end_date: formData.end_date
          ? formData.end_date.format("YYYY-MM-DD")
          : null,
        is_active: formData.is_active,
      };

      let response;
      if (isEditing) {
        response = await api.put(
          apiUrls.coaching.roleAssignments(role.uuid),
          payload
        );
      } else {
        response = await api.post(apiUrls.coaching.roleAssignments(), payload);
      }

      // Check the success field in the response
      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason ||
            `Failed to ${
              isEditing ? "update" : "create"
            } role assignment due to data validation issues`
        );
        return;
      }

      showSuccessSnackbar(
        `Role assignment ${isEditing ? "updated" : "created"} successfully`
      );
      onSave();
    } catch (error) {
      console.error("Error saving role assignment:", error);
      showErrorSnackbar(
        error.response?.data?.detail ||
          error.response?.data?.reason ||
          `Failed to ${isEditing ? "update" : "create"} role assignment`
      );
    } finally {
      setLoading(false);
    }
  };

  const drawerContent = (
    <Box sx={{ width: 400, p: 3, mt: `${layout.navBarHeight + 1}rem` }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">
          {isEditing ? "Edit Role Assignment" : "Add Role Assignment"}
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>

      <Stack spacing={3}>
        {/* Role Selection */}
        <FormControl fullWidth error={!!errors.role}>
          <InputLabel>Role</InputLabel>
          <Select
            value={formData.role}
            label="Role"
            onChange={(e) => handleFieldChange("role", e.target.value)}
          >
            {COACH_ROLES.map((roleOption) => (
              <MenuItem key={roleOption.value} value={roleOption.value}>
                {roleOption.label}
              </MenuItem>
            ))}
          </Select>
          {errors.role && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {errors.role}
            </Typography>
          )}
        </FormControl>

        {/* Date Range Picker */}
        <Box>
          <DateRangePicker
            startText="Start Date"
            endText="End Date"
            value={dateRange}
            onChange={handleDateRangeChange}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!(errors.start_date || errors.end_date),
                helperText: errors.start_date || errors.end_date,
              },
            }}
          />
        </Box>

        {/* Quick End Today Button */}
        {!formData.end_date && (
          <Button
            variant="outlined"
            startIcon={<EventBusy />}
            onClick={handleEndToday}
            size="small"
          >
            End Today
          </Button>
        )}

        {/* Active Switch */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={(e) => handleFieldChange("is_active", e.target.checked)}
            />
          }
          label="Active"
        />

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={loading}
          >
            {isEditing ? "Update" : "Create"}
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            boxShadow: (theme) => theme.shadows[8],
          },
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
